import type { ProgramAccount } from "@coral-xyz/anchor";
import { BN } from "@coral-xyz/anchor";
import { AccountLayout, MintLayout } from "@solana/spl-token";
import { useConnection } from "@solana/wallet-adapter-react";
import { PublicKey } from "@solana/web3.js";
import { apyToApr, convert, getUnixEpochSeconds, readOnlyAnchorWallet } from "@stepfinance/common";
import type { FarmStatus, StepRewardUser } from "@stepfinance/farms";
import { FarmType } from "@stepfinance/farms";
import type { StepFarmPosition } from "@stepfinance/farms/src/lib/positions/step/types";
import type { PendingReward } from "@stepfinance/liquidity";
import { getLiquidityPoolName } from "@stepfinance/liquidity";
import { TokenAccountParser } from "@stepfinance/tokens";
import BigNumber from "bignumber.js";
import { useEffect, useState } from "react";
import useDeepCompareEffect from "use-deep-compare-effect";

import { getStepRewardProgram, getStepRewardProvider } from "../../actions/step/reward-pool";
import { STEP_URL } from "../../constants";
import { getMultipleAccounts } from "../../contexts/accounts";
import { useEnrichedWallet } from "../../contexts/enrichedWallet";
import { useLanguageContext } from "../../contexts/language/language";
import { useLiquidity } from "../../contexts/liquidity";
import { useMarkets } from "../../contexts/market";
import {
  calculateRewardPerToken,
  calculateUserRewardABalance,
  calculateUserRewardBBalance,
  getRewardPoolApr,
} from "../../models/step/farms/utils";
import type { Wallet } from "../../types/wallet";
import { cache } from "../../utils/cache";
import { priceInUSDWrapper } from "../../utils/markets/getPriceInUSD";
import { getTotalLiquidityInUSD } from "../../utils/pools/utils";
import { getTokenByMint } from "../../utils/tokenInfo";
import { useConnectedWallet } from "../wallet/useConnectedWallet";

export type StepFarmMap = Map<string, StepFarmPosition>;
export type StepUserFarmMap = Map<string, ProgramAccount<StepRewardUser>>;

const FarmTypeLabelMap = {
  [FarmType.SINGLE_ASSET]: "Single Asset Staking",
  [FarmType.SINGLE_REWARD]: "Single Asset Yield",
  [FarmType.DUAL_REWARD]: "Dual Asset Yield",
};

export const useStepFarms = () => {
  const { connection } = useConnection();
  const { connected } = useEnrichedWallet();
  const wallet = useConnectedWallet();
  const { languageSetting } = useLanguageContext();
  const { stepFinancePools } = useLiquidity();
  const { marketsInitialised, priceInUSD } = useMarkets();
  const [farms, setFarms] = useState<StepFarmMap>(new Map());
  const [refreshCounter, setRefreshCounter] = useState(0);

  function refreshStepFarms() {
    setRefreshCounter(refreshCounter + 1);
  }

  useEffect(() => setFarms(new Map()), [connected]);

  useDeepCompareEffect(() => {
    if (!marketsInitialised || Object.keys(stepFinancePools).length === 0) {
      return;
    }

    void (async () => {
      const anchorWallet = wallet ? wallet : readOnlyAnchorWallet;
      const provider = getStepRewardProvider(connection, anchorWallet as Wallet);
      const program = getStepRewardProgram(provider);

      const farmMints: string[] = [];
      const farmAccounts: string[] = [];

      const stepRewardPools = await program.account.pool.all();
      const farmKeys = stepRewardPools.map((farm) => farm.publicKey);

      const userKeyFarmKeyMap: { [userStakePublicKey: string]: string } = {};
      let userFarmMap: StepUserFarmMap = new Map();
      if (wallet?.publicKey) {
        for (const farmKey of farmKeys) {
          const [userAccount] = await PublicKey.findProgramAddress(
            [wallet.publicKey.toBuffer(), farmKey.toBuffer()],
            program.programId,
          );
          userKeyFarmKeyMap[userAccount.toBase58()] = farmKey.toBase58();
        }

        const accountsCoder = program.coder.accounts;

        const { keys: userKeys, array: userArray } = await getMultipleAccounts(
          connection,
          Object.keys(userKeyFarmKeyMap),
          "single",
        );

        userFarmMap = userArray.reduce<StepUserFarmMap>((userFarmMap, user, index) => {
          if (!user?.data) {
            return userFarmMap;
          }
          const pubkey = userKeys[index];
          const account: StepRewardUser = accountsCoder.decode("user", user.data);
          return userFarmMap.set(userKeyFarmKeyMap[pubkey], {
            publicKey: new PublicKey(pubkey),
            account,
          });
        }, new Map());
      }
      // populate accounts in cache
      stepRewardPools.forEach((pool) => {
        const mints: string[] = [];
        const { stakingMint, stakingVault, rewardAVault, rewardAMint, rewardBVault, rewardBMint, authority } =
          pool.account;
        for (const mint of [stakingMint, rewardAMint, rewardBMint]) {
          if (!cache.hasMint(mint)) {
            mints.push(mint.toBase58());
          }
        }
        farmMints.push(...mints);
        farmAccounts.push(
          pool.publicKey.toBase58(),
          stakingVault.toBase58(),
          rewardAVault.toBase58(),
          rewardBVault.toBase58(),
          authority.toBase58(),
        );
      });

      const addresses = [...farmMints, ...farmAccounts];
      const { keys, array } = await getMultipleAccounts(connection, addresses, "single");
      array.forEach((accountInfo, index) => {
        const address = new PublicKey(keys[index]);

        if (!accountInfo) {
          return undefined;
        }

        if (accountInfo.data.length === AccountLayout.span) {
          return cache.add(address, accountInfo, TokenAccountParser);
        }

        if (accountInfo.data.length === MintLayout.span) {
          return cache.addMint(address, accountInfo);
        }
      });

      const stepFarms = stepRewardPools.reduce<Map<string, StepFarmPosition>>((farmMap, pool) => {
        const {
          stakingMint,
          stakingVault,
          rewardAMint,
          rewardAPerTokenStored,
          rewardARate,
          rewardBMint,
          rewardBPerTokenStored,
          rewardBRate,
          rewardDurationEnd,
          paused,
          version,
        } = pool.account;

        const farmVersion = version["v1"] ? 1 : 2;

        // Assumptions for farm type:
        // same reward mint is single_reward
        // staking mint not found in stepliquidity pools is single_asset
        let type: FarmType;
        if (!rewardAMint.equals(rewardBMint)) {
          type = FarmType.DUAL_REWARD;
        } else if (!Object.prototype.hasOwnProperty.call(stepFinancePools, stakingMint.toBase58())) {
          type = FarmType.SINGLE_ASSET;
        } else {
          type = FarmType.SINGLE_REWARD;
        }

        const liquidityPool = stepFinancePools[stakingMint.toBase58()];
        if (!liquidityPool) {
          //TODO Create path for single staked assets
          return farmMap;
        }
        const stakingVaultAccount = cache.get(stakingVault);
        const stakingMintInfo = cache.getMint(stakingMint.toBase58());
        const rewardAMintInfo = getTokenByMint(rewardAMint.toBase58());
        const rewardBMintInfo = getTokenByMint(rewardBMint.toBase58());
        const mintAInfo = getTokenByMint(liquidityPool?.mints[0]);
        const mintBInfo = getTokenByMint(liquidityPool?.mints[1]);
        if (!mintAInfo || !mintBInfo || !rewardAMintInfo || !rewardBMintInfo) {
          return farmMap;
        }
        let status: FarmStatus;
        const now = new BN(getUnixEpochSeconds());
        const timeRemaining = rewardDurationEnd.sub(now);
        const emissionsEnded = timeRemaining.lt(new BN(0)) && !rewardDurationEnd.isZero();

        if (paused || !mintAInfo || !mintBInfo) {
          status = "ended";
        } else if (rewardDurationEnd.eq(new BN(0)) || emissionsEnded) {
          status = "pending";
        } else {
          status = "active";
        }
        if (!stakingVaultAccount || !stakingMintInfo) {
          throw new Error("Farm vault or farm mint not found");
        }
        const userStake = userFarmMap.get(pool.publicKey.toBase58());

        const farmLPRatio = liquidityPool.supply
          ? new BigNumber(convert(stakingVaultAccount.info.amount, stakingMintInfo.decimals)).div(liquidityPool.supply)
          : new BigNumber(0);

        const userLPRatio = userStake
          ? new BigNumber(userStake.account.balanceStaked.toString()).div(stakingVaultAccount.info.amount.toString())
          : new BigNumber(0);

        const farmLiquidityA = farmLPRatio.multipliedBy(liquidityPool.liquidity[0]).toNumber();
        const farmLiquidityB = farmLPRatio.multipliedBy(liquidityPool.liquidity[1]).toNumber();
        const liquidityA = userLPRatio.multipliedBy(farmLiquidityA).toNumber();
        const liquidityB = userLPRatio.multipliedBy(farmLiquidityB).toNumber();

        const liquidity = [
          { token: mintAInfo, amount: liquidityA },
          { token: mintBInfo, amount: liquidityB },
        ];

        const farmLiquidityInUSD = farmLPRatio
          .multipliedBy(getTotalLiquidityInUSD(liquidityPool.liquidityInUSD))
          .toNumber();
        const liquidityInUSD = userLPRatio.toNumber() * farmLiquidityInUSD;

        const aprA = getRewardPoolApr({
          farmVersion,
          farmStatus: status,
          rewardRate: rewardARate,
          rewardMint: rewardAMint,
          rewardMintInfo: rewardAMintInfo,
          farmLiquidityInUSD,
          priceInUSD,
        });
        const aprB = getRewardPoolApr({
          farmVersion,
          farmStatus: status,
          rewardRate: rewardBRate,
          rewardMint: rewardBMint,
          rewardMintInfo: rewardBMintInfo,
          farmLiquidityInUSD,
          priceInUSD,
        });
        const apr = aprA + aprB;

        const feesAPR = apyToApr((liquidityPool.apy ?? 0) * 100);

        const rewardAPerToken = calculateRewardPerToken(
          pool.account,
          rewardAPerTokenStored,
          rewardARate,
          rewardAMintInfo?.decimals,
          new BN(stakingVaultAccount.info.amount.toString()),
        );
        const rewardBPerToken = calculateRewardPerToken(
          pool.account,
          rewardBPerTokenStored,
          rewardBRate,
          rewardBMintInfo?.decimals,
          new BN(stakingVaultAccount.info.amount.toString()),
        );

        const rewards: PendingReward[] = [];
        if (userStake) {
          const rewardABalanceLamports = calculateUserRewardABalance(userStake.account, rewardAPerToken);
          const rewardABalance = convert(rewardABalanceLamports, rewardAMintInfo.decimals);
          rewards.push({
            rewardToken: rewardAMintInfo,
            rewardBalance: rewardABalance,
            rewardInUSD: rewardABalance * priceInUSDWrapper(priceInUSD(rewardAMint.toBase58()) ?? 0),
          });
          if (type === FarmType.DUAL_REWARD) {
            const rewardBBalanceLamports = calculateUserRewardBBalance(userStake.account, rewardBPerToken);
            const rewardBBalance = convert(rewardBBalanceLamports, rewardAMintInfo.decimals);
            rewards.push({
              rewardToken: rewardBMintInfo,
              rewardBalance: rewardBBalance,
              rewardInUSD: rewardBBalance * priceInUSDWrapper(priceInUSD(rewardBMint.toBase58()) ?? 0),
            });
          }
        }
        const farmObject: StepFarmPosition = {
          key: pool.publicKey.toBase58(),
          kind: "Step Finance",
          lpName: getLiquidityPoolName(liquidityPool),
          lpMintAddress: liquidityPool.mintAddress,
          link: `${STEP_URL}/${languageSetting}/farms`,
          description: FarmTypeLabelMap[type],
          poolPublicKey: pool.publicKey,
          version: farmVersion,
          farmLiquidityA,
          farmLiquidityB,
          farmLiquidityInUSD,
          liquidity,
          liquidityPool,
          type,
          status,
          lp: stakingMintInfo as unknown as any,
          apr: apr ?? 0,
          aprA,
          aprB,
          feesApr: feesAPR / 100,
          userStake: userStake,
          rewards,
          rewardPool: pool,
          valueInUSD: liquidityInUSD,
        };
        return farmMap.set(pool.publicKey.toBase58(), farmObject);
      }, new Map());

      setFarms(stepFarms);
    })();
  }, [connection, marketsInitialised, priceInUSD, refreshCounter, stepFinancePools, wallet]);

  return { farms, refreshStepFarms };
};
